/**
 * Plan de pagos de una reserva con el modelo de hoy (4 %), para explicarle al
 * huésped QUÉ paga, CUÁNDO y A QUIÉN. Solo presentación: los montos salen de
 * la simulación de la API (PriceService::getReservationPrices) y acá se
 * reparten por tramos con sus fechas.
 *
 * Reglas (las mismas del backend):
 *  - Un mes son 30 noches. Precio por noche = arriendo mensual / 30.
 *  - Estadías de 30 noches o más: hoy se paga por la plataforma el primer mes
 *    (las primeras 30 noches) + el cargo por servicio. Los meses siguientes y
 *    las noches que sobran después del último mes completo se le pagan
 *    directo al anfitrión (`remainingToHost` = estadía total − primer mes).
 *  - Menos de 30 noches: hoy se paga la estadía completa + el cargo.
 *
 * Fechas: 'YYYY-MM-DD'. Se calculan sin zona horaria (mediodía local) para que
 * no se corran un día.
 */

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

function sumarDias(iso, n) {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d, 12);
    dt.setDate(dt.getDate() + n);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/**
 * @param {object} p
 * @param {number} p.noches        noches de la estadía
 * @param {string} p.llegada       'YYYY-MM-DD'
 * @param {number} p.mensual       arriendo mensual (30 noches)
 * @param {number} p.primerMes     lo que se cobra hoy de arriendo
 * @param {number} p.restoAnfitrion lo que queda para el anfitrión
 * @returns {{ completos:number, extra:number, porNoche:number, hoy:object, despues:Array }}
 *   hoy:     { desde, hasta, noches, monto }  (el primer mes o la estadía entera)
 *   despues: [{ tipo:'mes', n, desde, hasta, monto } | { tipo:'noches', noches, desde, hasta, monto }]
 */
export function planDePagos({ noches, llegada, mensual, primerMes, restoAnfitrion }) {
    const n = Math.max(0, Number(noches) || 0);
    const mes = Number(mensual) || 0;
    const resto = Math.max(0, Number(restoAnfitrion) || 0);
    const conFecha = FECHA_RE.test(String(llegada || ''));
    const fecha = d => (conFecha ? sumarDias(llegada, d) : null);

    const completos = n >= 30 ? Math.floor(n / 30) : 0;
    const extra = n >= 30 ? n % 30 : 0;
    const nochesHoy = n >= 30 ? 30 : n;

    const plan = {
        completos,
        extra,
        porNoche: mes ? mes / 30 : 0,
        hoy: { desde: fecha(0), hasta: fecha(nochesHoy), noches: nochesHoy, monto: Number(primerMes) || 0 },
        despues: [],
    };
    if (n < 30 || resto <= 0) return plan;

    // Meses completos después del primero. Si no sobran noches, el último mes
    // se lleva lo que falte para cerrar exacto con el resto de la API.
    let asignado = 0;
    for (let k = 2; k <= completos; k++) {
        const ultimo = k === completos && extra === 0;
        const monto = ultimo ? Math.max(0, resto - asignado) : mes;
        plan.despues.push({ tipo: 'mes', n: k, desde: fecha(30 * (k - 1)), hasta: fecha(30 * k), monto });
        asignado += monto;
    }
    if (extra > 0) {
        plan.despues.push({ tipo: 'noches', noches: extra, desde: fecha(30 * completos), hasta: fecha(n), monto: Math.max(0, resto - asignado) });
    }
    return plan;
}
